import React from 'react';

import type {
  MatchingLevel,
  MatchingMetaDto,
  MatchingPreferenceDto,
  MatchingTargetType,
} from '../../api/matchingTypes';
import { LEVEL_LABEL, setPreference } from '../../domain/matching/matching';

import './matching.css';

type Props = {
  meta: MatchingMetaDto;
  value: MatchingPreferenceDto[];
  disabled?: boolean;
  onChange: (next: MatchingPreferenceDto[]) => void;
};

type Option = { id: number; name: string };

const GROUPS: { level: MatchingLevel; icon: string; hint: string }[] = [
  { level: 'WANT', icon: '✅', hint: 'Обязательно: сыграем только во что-то из этих списков' },
  { level: 'ACCEPTABLE', icon: '👍', hint: 'Не обязательно, но поможет подобрать компанию' },
  { level: 'EXCLUDE', icon: '🚫', hint: 'Точно нет: эти игры и теги не подойдут' },
];

type ZoneProps = {
  title: string;
  placeholder: string;
  options: Option[];
  selectedIds: number[];
  takenIds: Set<number>;
  limitReached: boolean;
  /** Shown as a non-removable chip while nothing is chosen; replaced by the first real choice. */
  defaultLabel?: string;
  disabled?: boolean;
  onAdd: (id: number) => void;
  onRemove: (id: number) => void;
};

/** One zone of a level: the chosen items plus a select that adds the picked item immediately. */
function PreferenceZone({
  title,
  placeholder,
  defaultLabel,
  options,
  selectedIds,
  takenIds,
  limitReached,
  disabled,
  onAdd,
  onRemove,
}: ZoneProps) {
  const available = options.filter((o) => !takenIds.has(o.id));
  const nameOf = (id: number) => options.find((o) => o.id === id)?.name ?? String(id);

  // Picking an item adds it right away; the select stays on the placeholder.
  const add = (value: string) => {
    const id = Number(value);
    if (disabled || limitReached || !available.some((o) => o.id === id)) return;
    onAdd(id);
  };

  return (
    <div className="matching-prefs__zone">
      <div className="ttbFieldLabel">{title}</div>

      {selectedIds.length > 0 || defaultLabel ? (
        <div className="matching-prefs__chips">
          {selectedIds.length === 0 && defaultLabel ? (
            <span className="matching-prefs__chip matching-prefs__chip--static">{defaultLabel}</span>
          ) : null}
          {selectedIds.map((id) => (
            <span key={id} className="matching-prefs__chip">
              {nameOf(id)}
              <button
                type="button"
                className="matching-prefs__remove"
                aria-label={`Убрать ${nameOf(id)}`}
                disabled={disabled}
                onClick={() => onRemove(id)}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div className="matching-prefs__adders">
        <select
          className="ttbInput matching-prefs__add"
          value=""
          disabled={disabled || limitReached || available.length === 0}
          aria-label={title}
          onChange={(e) => add(e.target.value)}
        >
          <option value="">
            {limitReached ? 'Лимит достигнут' : available.length === 0 ? 'Список пуст' : placeholder}
          </option>
          {available.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

type GroupProps = {
  level: MatchingLevel;
  icon: string;
  hint: string;
  defaultOpen: boolean;
  meta: MatchingMetaDto;
  value: MatchingPreferenceDto[];
  disabled?: boolean;
  onChange: (next: MatchingPreferenceDto[]) => void;
};

function PreferenceGroup({ level, icon, hint, defaultOpen, meta, value, disabled, onChange }: GroupProps) {
  const [open, setOpen] = React.useState(defaultOpen);

  const options: Record<MatchingTargetType, Option[]> = {
    TAG: meta.tags.map((t) => ({ id: t.id, name: t.name })),
    GAME: meta.games.map((g) => ({ id: g.id, name: g.name })),
  };
  const limits: Record<MatchingTargetType, number> = {
    TAG: meta.limits.maxTagPreferences,
    GAME: meta.limits.maxGamePreferences,
  };

  const inLevel = (type: MatchingTargetType) =>
    value.filter((p) => p.level === level && p.targetType === type).map((p) => p.targetId);
  const takenAnywhere = (type: MatchingTargetType) =>
    new Set(value.filter((p) => p.targetType === type).map((p) => p.targetId));
  const total = (type: MatchingTargetType) => value.filter((p) => p.targetType === type).length;
  const levelCount = value.filter((p) => p.level === level).length;

  const zone = (type: MatchingTargetType, title: string, placeholder: string, defaultLabel?: string) => (
    <PreferenceZone
      title={title}
      placeholder={placeholder}
      defaultLabel={defaultLabel}
      options={options[type]}
      selectedIds={inLevel(type)}
      takenIds={takenAnywhere(type)}
      limitReached={total(type) >= limits[type]}
      disabled={disabled}
      onAdd={(id) => onChange(setPreference(value, type, id, level))}
      onRemove={(id) => onChange(setPreference(value, type, id, null))}
    />
  );

  return (
    <div className="matching-prefs__group">
      <button
        type="button"
        className="matching-prefs__group-header"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>
          <span aria-hidden="true">{icon}</span> {LEVEL_LABEL[level]}
          {levelCount > 0 ? <span className="matching-prefs__count">{levelCount}</span> : null}
        </span>
        <span className="matching-prefs__arrow" aria-hidden="true">
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open ? (
        <div className="matching-prefs__group-body">
          <div className="ttbFieldHint">{hint}</div>
          {zone('TAG', 'Теги', 'Выберите тег')}
          {zone('GAME', 'Игры', 'Выберите игру', level === 'ACCEPTABLE' ? 'Все игры' : undefined)}
        </div>
      ) : null}
    </div>
  );
}

/** Read-only recap of what the request will contain, one row per level; "Буду" defaults to "Все игры". */
function PreferenceSummary({ meta, value }: { meta: MatchingMetaDto; value: MatchingPreferenceDto[] }) {
  const nameOf = (p: MatchingPreferenceDto) =>
    (p.targetType === 'TAG' ? meta.tags : meta.games).find((o) => o.id === p.targetId)?.name ?? String(p.targetId);

  return (
    <div className="matching-prefs__summary" aria-label="Что уйдёт в заявке">
      <div className="ttbFieldLabel">Ваша заявка</div>
      {GROUPS.map(({ level, icon }) => {
        const names = value.filter((p) => p.level === level).map(nameOf);
        const labels = names.length === 0 && level === 'ACCEPTABLE' ? ['Все игры'] : names;
        return (
          <div key={level} className="matching-prefs__summary-row">
            <span className="matching-prefs__summary-level">
              <span aria-hidden="true">{icon}</span> {LEVEL_LABEL[level]}
            </span>
            {labels.length > 0 ? (
              <span className="matching-prefs__chips">
                {labels.map((label) => (
                  <span key={label} className="matching-prefs__chip matching-prefs__chip--static">
                    {label}
                  </span>
                ))}
              </span>
            ) : (
              <span className="matching-prefs__summary-empty">—</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Game/tag preferences: three collapsible levels (Хочу / Буду / Не хочу); a target has at most one level. */
export function PreferencePicker({ meta, value, disabled, onChange }: Props) {
  const noGames = meta.games.length === 0;
  const noTags = meta.tags.length === 0;

  return (
    <div className="matching-prefs">
      {noGames || noTags ? (
        <div className="ttbFieldHint">
          {noGames && noTags
            ? 'В каталоге пока нет игр и тегов для сбора'
            : noGames
              ? 'В каталоге пока нет игр для сбора'
              : 'В каталоге пока нет тегов'}
        </div>
      ) : null}

      <PreferenceSummary meta={meta} value={value} />

      {GROUPS.map((g) => (
        <PreferenceGroup
          key={g.level}
          {...g}
          defaultOpen={g.level === 'WANT' || value.some((p) => p.level === g.level)}
          meta={meta}
          value={value}
          disabled={disabled}
          onChange={onChange}
        />
      ))}

      <div className="ttbFieldHint">
        Теги: {value.filter((p) => p.targetType === 'TAG').length}/{meta.limits.maxTagPreferences} · Игры:{' '}
        {value.filter((p) => p.targetType === 'GAME').length}/{meta.limits.maxGamePreferences}
      </div>
    </div>
  );
}
